import { useTranslation } from "react-i18next";
import ChevronSvg from "@/assets/svgTsx/chevron";
import { GetAllBillingEntityDto, GetUsersBoResponse } from "@/types/api/api-types";
import { StyledSelectWoController } from "@/components/styled/StyledSelectWoController";

type FiltersProps = {
  setTimeScale: React.Dispatch<React.SetStateAction<"year" | "month" | "week">>;
  timeScale: "year" | "month" | "week";
  setYear: React.Dispatch<React.SetStateAction<number>>;
  year: number;
  setMonth: React.Dispatch<React.SetStateAction<number>>;
  month: number;
  setWeek: React.Dispatch<React.SetStateAction<number>>;
  week: number;
  setSource: React.Dispatch<React.SetStateAction<"all" | "cotations" | "customCotations">>;
  source: "all" | "cotations" | "customCotations";
  setUserId: React.Dispatch<React.SetStateAction<string | undefined>>;
  users: GetUsersBoResponse;
  setBillingEntityId: React.Dispatch<React.SetStateAction<string | undefined>>;
  billingEntities: GetAllBillingEntityDto;
};

export const Filters = ({
  setTimeScale,
  timeScale,
  setYear,
  year,
  setMonth,
  month,
  setWeek,
  week,
  setSource,
  setUserId,
  users,
  billingEntities,
  setBillingEntityId,
}: FiltersProps) => {
  const { t } = useTranslation("boClients");
  const handleWeekChange = (week: number) => {
    if (week < 1 || week > 52) return;
    setWeek(week);
  };

  const handleMonthChange = (month: number) => {
    if (month < 0 || month > 11) return;
    setMonth(month);
  };

  const handleYearChange = (year: number) => {
    if (year > new Date().getFullYear()) return;
    setYear(year);
  };

  return (
    <div className="flex flex-col justify-between gap-3 md:flex-row">
      <div className="flex w-full flex-row flex-wrap gap-3 md:w-fit">
        <div className="hidden w-fit flex-row gap-2 rounded-lg border border-gray-300 bg-white p-0.5 md:flex">
          <button
            className={`rounded-lg px-4 py-2 text-sm font-medium focus:outline-hidden ${timeScale === "week" ? "bg-brand700 text-white" : "bg-white text-gray-700"}`}
            onClick={() => setTimeScale("week")}
          >
            {t("profileStatisticsFilters.timeScales.week")}
          </button>
          <button
            className={`rounded-lg px-4 py-2 text-sm font-medium focus:outline-hidden ${timeScale === "month" ? "bg-brand700 text-white" : "bg-white text-gray-700"}`}
            onClick={() => setTimeScale("month")}
          >
            {t("profileStatisticsFilters.timeScales.month")}
          </button>
          <button
            className={`rounded-lg px-4 py-2 text-sm font-medium focus:outline-hidden ${timeScale === "year" ? "bg-brand700 text-white" : "bg-white text-gray-700"}`}
            onClick={() => setTimeScale("year")}
          >
            {t("profileStatisticsFilters.timeScales.year")}
          </button>
        </div>
        <select
          onChange={(e) => setSource(e.target.value as "all" | "cotations" | "customCotations")}
          className="w-full rounded-lg border border-gray-300 bg-white p-0.5 md:w-fit"
        >
          <option value="all">{t("profileStatisticsFilters.sources.all")}</option>
          <option value="cotations">{t("profileStatisticsFilters.sources.cotations")}</option>
          <option value="customCotations">
            {t("profileStatisticsFilters.sources.customCotations")}
          </option>
        </select>
        <select
          onChange={(e) => setUserId(e.target.value === "" ? undefined : e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white p-0.5 md:w-fit"
        >
          <option value={"undefined"}>{t("profileStatisticsFilters.allUsers")}</option>
          {users?.map((user) => (
            <option key={user.id} value={user.id}>
              {user.firstName} {user.lastName}
            </option>
          ))}
        </select>
        {Array.isArray(billingEntities) && billingEntities.length > 0 && (
          <select
            onChange={(e) => setBillingEntityId(e.target.value === "" ? undefined : e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white p-0.5 md:w-fit"
          >
            <option value={"undefined"}>
              {t("profileStatisticsFilters.billingEntityPlaceholder")}
            </option>
            {billingEntities?.map((billingEntity) => (
              <option key={billingEntity.id} value={billingEntity.id}>
                {billingEntity.deletedAt
                  ? t("profileStatisticsFilters.deletedBillingEntity", {
                      companyName: billingEntity.companyName,
                    })
                  : billingEntity.companyName}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex w-full flex-row flex-wrap gap-3 md:w-fit">
        <div className="flex min-w-[200px] flex-1 md:hidden">
          <StyledSelectWoController
            placeholder={t("profileStatisticsFilters.timeScalePlaceholder")}
            isClearable={false}
            isSearchable={false}
            options={[
              { label: t("profileStatisticsFilters.timeScales.week"), value: "week" },
              { label: t("profileStatisticsFilters.timeScales.month"), value: "month" },
              { label: t("profileStatisticsFilters.timeScales.year"), value: "year" },
            ]}
            onChange={(e) => setTimeScale(e?.value || "")}
            className="flex-1"
            value={timeScale}
          />
        </div>
        {timeScale === "week" && (
          <div className="flex flex-1 flex-row items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2">
            <button
              onClick={() => handleWeekChange(week - 1)}
              className="rotate-180 focus:outline-hidden"
            >
              <ChevronSvg className="size-3 md:size-4" color="#000" />
            </button>
            <span className="truncate">{t("profileStatisticsFilters.weekNumber", { week })}</span>
            <button onClick={() => handleWeekChange(week + 1)} className="focus:outline-hidden">
              <ChevronSvg className="size-3 md:size-4" color="#000" />
            </button>
          </div>
        )}
        {timeScale === "month" && (
          <div className="flex flex-1 flex-row items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 md:w-[200px]">
            <button
              onClick={() => handleMonthChange(month - 1)}
              className="rotate-180 focus:outline-hidden"
            >
              <ChevronSvg className="size-3 md:size-4" color="#000" />
            </button>
            <span className="capitalize">
              {
                "janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre".split(
                  "_",
                )[month]
              }
            </span>
            <button onClick={() => handleMonthChange(month + 1)} className="focus:outline-hidden">
              <ChevronSvg className="size-3 md:size-4" color="#000" />
            </button>
          </div>
        )}
        <div className="flex flex-1 flex-row items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2">
          <button
            onClick={() => handleYearChange(year - 1)}
            className="rotate-180 focus:outline-hidden"
          >
            <ChevronSvg className="size-3 md:size-4" color="#000" />
          </button>
          <span>{year}</span>
          <button onClick={() => handleYearChange(year + 1)} className="focus:outline-hidden">
            <ChevronSvg className="size-3 md:size-4" color="#000" />
          </button>
        </div>
      </div>
    </div>
  );
};
